import React, { useState } from "react";
import { Button, Form } from "react-bootstrap";
import { ArrowLeft, Person, Envelope, Trophy, BarChart, FileEarmarkText, Gear } from "react-bootstrap-icons";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Page from "../../Page";
import storeApis from "../../../api/storeApi";
import Toast from "../../shared/Toast";
import "./Contests.css";

const UPLOAD_CONTEST_URL = "/framework/api/CHLIRnR/upload-contest";

const DB_FIELDS = [
  { key: "participant_id", label: "Participant ID", required: true, Icon: Gear },
  { key: "participant_name", label: "Participant Name", required: true, Icon: Person },
  { key: "email", label: "Email Address", required: true, Icon: Envelope },
  { key: "target_value", label: "Target Value", required: true, Icon: Trophy },
  { key: "score", label: "Score / Value", required: true, Icon: BarChart },
];

const DEFAULT_MAPPING = {
  participant_id: "",
  participant_name: "",
  email: "",
  target_value: "",
  score: "",
};

const SAMPLE_ROW = {
  "Unique ID": "user_849201",
  Name: "Sarah Johnson",
  Email: "sarah.j@example.com",
  "Contest Name": "Dubai Dream Challenge",
  "Current Progress": "4500",
};

const AUTO_MAP_ALIASES = {
  participant_id: ["unique id", "participant id", "user id", "id"],
  participant_name: ["name", "participant name", "full name"],
  email: ["email", "email address", "mail"],
  target_value: ["target", "target value", "contest name", "target contest", "contest", "value"],
  score: ["score", "score value", "current progress", "progress"],
};

const normalizeHeader = (header) =>
  String(header || "")
    .trim()
    .toLowerCase();

const buildAutoMapping = (headers) => {
  const normalizedHeaders = headers.map((h) => ({
    raw: h,
    norm: normalizeHeader(h),
  }));

  const nextMapping = { ...DEFAULT_MAPPING };
  Object.keys(nextMapping).forEach((key) => {
    const aliases = AUTO_MAP_ALIASES[key] || [];
    const match = normalizedHeaders.find((h) => aliases.includes(h.norm));
    if (match) nextMapping[key] = match.raw;
  });

  return nextMapping;
};

const formatFileSize = (bytes) => {
  const size = Number(bytes) || 0;
  if (size < 1024) return `${size}B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1).replace(/\.0$/, "")}KB`;
  return `${(size / (1024 * 1024)).toFixed(1).replace(/\.0$/, "")}MB`;
};

const fileToContestBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      const base64Part = dataUrl.includes(",") ? dataUrl.split(",")[1] : dataUrl;
      resolve(`data:@file/csv;base64,${base64Part}`);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const ContestCSVImportPage = (props) => {
  props.pageTypeHandler?.("Contests");
  const navigate = useNavigate();
  const { id: contestIdFromRoute } = useParams();
  const location = useLocation();
  const authToken = props.authToken;
  const contestId = String(
    location.state?.contest?.id ??
    contestIdFromRoute ??
    ""
  );

  const [file, setFile] = useState(null);
  const [fileInfo, setFileInfo] = useState({ size: "", rows: "" });
  const [mapping, setMapping] = useState(DEFAULT_MAPPING);
  const [sampleRow, setSampleRow] = useState(SAMPLE_ROW);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(2); // 1 = Upload File, 2 = Map Fields (current in design)

  const fileInputRef = React.useRef(null);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    const formattedSize = formatFileSize(f.size);
    setFileInfo({ size: formattedSize, rows: "—" });
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result || "";
      const lines = text.split("\n").filter((l) => l.trim());
      const headers = (lines[0] || "").split(",").map((h) => h.trim());
      setCsvHeaders(headers.filter(Boolean));
      setMapping(buildAutoMapping(headers));
      setFileInfo({ size: formattedSize, rows: String(Math.max(lines.length - 1, 0)) });
      const row1 = {};
      if (lines[1]) {
        const values = lines[1].split(",").map((v) => v.trim());
        headers.forEach((h, i) => { row1[h] = values[i] ?? ""; });
        setSampleRow(row1);
      } else {
        setSampleRow({});
      }
      setStep(2);
    };
    reader.readAsText(f);
  };

  const setMappingFor = (fieldKey, csvHeader) => {
    setMapping((prev) => ({ ...prev, [fieldKey]: csvHeader }));
  };

  const handleBack = () => {
    navigate("/contests");
  };

  const handleCancel = () => {
    navigate("/contests");
  };

  const handleImport = async () => {
    if (!file) {
      Toast("Please upload a CSV file", "E");
      return;
    }
    if (!contestId) {
      Toast("Contest ID is missing", "E");
      return;
    }
    const missingField = DB_FIELDS.find((field) => !mapping[field.key]);
    if (missingField) {
      Toast("All fields are required", "E");
      return;
    }
    if (!authToken) {
      Toast("Authentication token is missing", "E");
      return;
    }

    setLoading(true);
    try {
      const encodedFile = await fileToContestBase64(file);
      const payload = {
        contest_id: contestId,
        mapping_data: {
          participant_id: mapping.participant_id,
          participant_name: mapping.participant_name,
          email: mapping.email,
          target_value: mapping.target_value,
          score: mapping.score,
        },
        file: encodedFile,
      };

      const response = await storeApis.post(UPLOAD_CONTEST_URL, payload, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (response.status === 200) {
        if (response.data.statusCode === "403") {
          Toast(response.data.message || "Unauthorized", "E");
        } else if (response.data.status === true || response.data.status === "Success") {
          Toast(response.data.message || "Contest CSV uploaded successfully", "S");
          navigate("/contests");
        } else {
          Toast(response.data.message || "Upload failed", "E");
        }
      }
    } catch (err) {
      Toast(err.response?.data?.message || err.response?.message || "Failed to upload contest CSV", "E");
    } finally {
      setLoading(false);
    }
  };
  const displayFileName = file?.name ?? "No file selected";
  const displayFileInfo = file
    ? `${fileInfo.size || "—"} • ${fileInfo.rows || "—"} rows detected`
    : "";

  return (
    <Page title={props.title || "Import Progress Data"}>
      <div className="contest-csv-import-page">
        <Button
          variant="link"
          className="csv-import-back-link p-0 text-decoration-none"
          onClick={handleBack}
        >
          <ArrowLeft size={18} className="me-1" />
          Back to Contests
        </Button>

        <h1 className="csv-import-title">Import Progress Data</h1>
        <p className="csv-import-subtitle">
          Map columns from your uploaded CSV file to the database fields.
        </p>

        <div className="csv-import-steps">
          <div className={`csv-import-step ${step >= 1 ? "completed" : ""}`}>
            <span className="csv-import-step-num">1</span>
            <span className="csv-import-step-label">Upload File</span>
            {step >= 1 && <span className="csv-import-step-check">✓</span>}
          </div>
          <span className="csv-import-steps-connector" aria-hidden="true" />
          <div className={`csv-import-step ${step >= 2 ? "active" : ""}`}>
            <span className="csv-import-step-num">2</span>
            <span className="csv-import-step-label">Map Fields</span>
          </div>
        </div>

        <div className="csv-import-file-info">
          <div className="csv-import-file-meta">
            <FileEarmarkText size={24} className="csv-import-file-icon" />
            <div>
              <span className="csv-import-file-name">{displayFileName}</span>
              <span className="csv-import-file-size">{displayFileInfo}</span>
            </div>
          </div>
          <Button
            variant="outline-secondary"
            size="sm"
            className="csv-import-change-file"
            onClick={() => fileInputRef.current?.click()}
          >
            Change File
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="d-none"
            onChange={handleFileChange}
          />
        </div>

        <div className="csv-import-mapping-card">
          <div className="csv-import-mapping-header">
            <div className="csv-import-col-db">Database Field</div>
            <div className="csv-import-col-arrow"></div>
            <div className="csv-import-col-csv">CSV Column Header</div>
            <div className="csv-import-col-sample">Sample Data (Row 1)</div>
          </div>
          {DB_FIELDS.map(({ key, label, required, Icon }) => (
            <div key={key} className="csv-import-mapping-row">
              <div className="csv-import-col-db csv-import-db-field">
                <Icon size={18} className="csv-import-field-icon me-2" />
                {label}
                {required && <span className="text-danger ms-1">*</span>}
              </div>
              <div className="csv-import-col-arrow">
                <span className="csv-import-arrow" aria-hidden="true">→</span>
              </div>
              <div className="csv-import-col-csv">
                <Form.Select
                  value={mapping[key] || ""}
                  onChange={(e) => setMappingFor(key, e.target.value)}
                  className="csv-import-select"
                >
                  <option value="">Select column</option>
                  {csvHeaders.map((h) => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </Form.Select>
              </div>
              <div className="csv-import-col-sample csv-import-sample-data">
                {sampleRow[mapping[key]] ?? "—"}
              </div>
            </div>
          ))}
        </div>

        {file && csvHeaders.length > 0 && (
          <div className="csv-import-mapping-card mt-3">
            <div className="csv-import-mapping-header">
              <div className="csv-import-col-db">CSV Sample Row (Row 1)</div>
            </div>
            <div className="p-3">
              <div className="table-responsive">
                <table className="table table-sm mb-0">
                  <thead>
                    <tr>
                      {csvHeaders.map((h) => (
                        <th key={h}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {csvHeaders.map((h) => (
                        <td key={h}>{sampleRow[h] ?? "—"}</td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <div className="csv-import-footer">
          <Button variant="outline-secondary" className="btn-footer-cancel" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            className="btn-footer-publish"
            onClick={handleImport}
            disabled={loading}
          >
            {loading ? "Importing…" : "Import"}
          </Button>
        </div>
      </div>
    </Page>
  );
};

export default ContestCSVImportPage;
